/**
 * Ryan O'Dowd
 * 2024-08-02
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import Button from '@components/common/Button';
// @TODO: import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
import {
  IconButton,
} from 'react-native-paper';
import Menu from '@components/common/Menu';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';
import {
  // @TODO: READ_ROUTE,
  // @TODO: addToReadingHistory,
  updateUserSettings,
} from '@utils/store/actions';
import utilities from '@utils/utilities';

const wpmOptions = [100, 200, 300, 400, 500, 600, 700, 800];

const SpritzDialog = (props: SpritzDialogProps) => {
  // @TODO: const navigation = hooks.useNavigation();
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const controlIconSize = styles.globalScale * 18;

  const [_isPaused, setIsPaused] = useState(true);
  const wordOffset = useRef(0);
  const [_currVerseNumber, setCurrVerseNumber] = useState(props.passage.startVerseNumber);
  const currVerseNumber = useRef(_currVerseNumber);
  const spritzWpm = hooks.useTypedSelector((state) => state.userSettings.spritz_wpm);
  const nextWordInterval = useRef(undefined);

  const totalMsSpent = useRef(0);
  const numVersesReviewed = useRef(0);
  const sessionStartTimestamp = useRef(null);

  const user = hooks.useTypedSelector((state) => state.oakUser);

  const [_startAnimationProgress, setStartAnimationProgress] = useState(0);
  const [_isWpmMenuOpen, setIsWpmMenuOpen] = useState(false);

  const passageVerses = hooks.useGetVersesForPassages([props.passage])[props.passage.id];// @TODO: possible index error? // @TODO: possible issue if verse texts aren't already in redux?
  const getWordsInVerse = useCallback(() => {
    return passageVerses[currVerseNumber.current - props.passage.startVerseNumber]?.verseText?.split(/[\s—–\n-]+/)?.map((w) => w.trim()).filter((w) => w) || []; // @TODO: shouldn't need `|| []` // @TODO: maybe don't split on single hyphens?
  }, [props.passage, passageVerses]);
  const [_wordToDisplay, setWordToDisplay] = useState(getWordsInVerse()[0] || '');

  const startSpritzing = useCallback((wordToDisplayOverride = undefined) => {
    const wordsInVerse = getWordsInVerse();
    sessionStartTimestamp.current = Date.now();
    setIsPaused(true);
    setWordToDisplay(wordToDisplayOverride || wordsInVerse[wordOffset.current]); // @TODO: possible issue with missing verses?
    setStartAnimationProgress(1);
  }, [getWordsInVerse]);

  const stopSpritzing = useCallback((wordToDisplayOverride = undefined) => {
    if (sessionStartTimestamp.current !== null) {
      totalMsSpent.current += Date.now() - sessionStartTimestamp.current;
    }
    sessionStartTimestamp.current = null;

    setIsPaused(true);
    setWordToDisplay(wordToDisplayOverride || _wordToDisplay);
  }, [_wordToDisplay]);

  const handleClose = () => {
    stopSpritzing();

    if (totalMsSpent.current >= 10 * 1000) { // @TODO: is this an okay threshold? only save lesson if at least 10 seconds?
      utilities.saveLesson({
        userId: user.id,
        passageId: props.passage.id,
        millisecondsSpent: totalMsSpent.current,
        peanutsEarned: Math.ceil(totalMsSpent.current / 1000 / 30), // round up to the nearest 30 seconds and award 1 peanuts per 30 seconds
        difficulty: 'spritz',
        numberOfVersesReviewed: numVersesReviewed.current,
        numberOfQuestions: 0,
        minigameIds: [],
        goalId: null, // @TODO: could be part of a goal
        testPassedVerseNums: null,
        assignmentId: props.assignmentId,
        adHocPassage: props.passage.id === -1 ? props.passage : null, // @TODO: this will only be the _last_ passage (if reading multiple chapters, for example)
        streakRepairDate: null,
        passageOfTheDayId: null,
        numberOfSkippedQuestions: 0,
      });
    }

    props.onClose();
  };

  const headingsInPassageByVerseOffset = [];
  let currVerseCount = 0;
  let currHeadingVerse = passageVerses[0];
  passageVerses.forEach((verse, verseOffset) => {
    if (verse.heading || verseOffset > 0) {
      currVerseCount++;
    }

    if (verse.heading && verseOffset > 0) {
      // this is not the first iteration of the loop
      headingsInPassageByVerseOffset.push({
        headingText: currHeadingVerse.heading,
        verseNumber: +currHeadingVerse.verseApiId.split('.')[2],
        numVersesInSection: currVerseCount,
      });
    }

    if (verse.heading) {
      currHeadingVerse = verse;
      currVerseCount = 0;
    }
  });
  headingsInPassageByVerseOffset.push({
    headingText: currHeadingVerse?.heading || '', // @TODO: magic string
    verseNumber: +currHeadingVerse?.verseApiId.split('.')[2] || props.passage.startVerseNumber,
    numVersesInSection: currHeadingVerse ? currVerseCount + 1 : currVerseCount,
  });

  useEffect(() => {
    if (_isPaused && _startAnimationProgress > 0) {
      if (_startAnimationProgress < 100) {
        setTimeout(() => {
          setStartAnimationProgress((curr) => curr ? curr + 5 : curr); // @TODO: is this too hacky? this ensures that double/triple/etc. tapping rewind or fast forward doesn't break the animation
        }, 20);
      } else {
        setIsPaused(false);
        setStartAnimationProgress(0);
      }
    }
  }, [_startAnimationProgress, _isPaused]);

  useEffect(() => {
    if (_isPaused || currVerseNumber.current > props.passage.endVerseNumber) {
      setWordToDisplay(' ');
      return () => {
        clearInterval(nextWordInterval.current);
      };
    }

    nextWordInterval.current = setInterval(() => {
      const wordsInVerse = getWordsInVerse();
      setWordToDisplay(wordsInVerse[wordOffset.current]); // @TODO: possible issue with missing verses?

      const isLastWord = wordOffset.current > wordsInVerse.length - 1;
      if (isLastWord) {
        numVersesReviewed.current += 1;
        // @TODO: don't love that these are coupled...needed right now because of setInterval {
        const newVal = currVerseNumber.current + 1;
        currVerseNumber.current = newVal;
        setCurrVerseNumber(newVal);
        if (newVal > props.passage.endVerseNumber) {
          stopSpritzing();
        }
        // @TODO: }
        wordOffset.current = 0;
      } else {
        wordOffset.current += 1;
      }
    }, (1000 * 60) / spritzWpm);

    return () => {
      clearInterval(nextWordInterval.current);
    };
  }, [props.passage, _isPaused, passageVerses, spritzWpm, startSpritzing, stopSpritzing, _wordToDisplay, getWordsInVerse]);

  const buttonColor = styles.isDarkMode ? styles.colors.primaryLight : styles.colors.primary;
  const buttonTextColor = styles.isDarkMode ? styles.colors.white : styles.colors.primaryExtraLight;

  return (
    <Dialog
      visible={true}
      onDismiss={() => handleClose()}
    >
      <Dialog.Content>
        <Text style={styles.dialogTitle}>{props.passage.prettyPassageName()}</Text>

        <View style={styles.wordAndControlsWrapper}>
          <View style={styles.wordContainer}>
            <View style={[styles.centerLine, styles.centerLineTop]} />
            <View style={[styles.centerLine, styles.centerLineBottom]} />
            <Text style={styles.mainWord}>{_wordToDisplay || ' '}</Text>{/* @NOTE: space is there to maintain container height even when finised */}
            {(_startAnimationProgress > 0 && _startAnimationProgress < 100) &&
              <View
                style={[
                  styles.startAnimationView,
                  {width: `${100 - _startAnimationProgress}%`},
                ]}
              />
            }
            <View style={styles.statsWrapper}>
              <Menu
                statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
                anchor={
                  <TouchableOpacity
                    style={styles.statsInner}
                    onPress={() => {
                      stopSpritzing();
                      setIsWpmMenuOpen(true);
                    }}
                  >
                    <Text style={styles.spritzWpm}>{spritzWpm}</Text>
                    <Text style={styles.currentVerseNumber}>v{_currVerseNumber}</Text>
                  </TouchableOpacity>
                }
                visible={_isWpmMenuOpen}
                onDismiss={() => setIsWpmMenuOpen(false)}
              >
                {wpmOptions.map((wpmOption) => {
                  return (
                    <Menu.Item
                      key={wpmOption}
                      title={wpmOption}
                      titleStyle={wpmOption === spritzWpm && styles.selectedMenuItem}// @TODO: convert this to a `selected` prop in a custom (wrapper) component for menu items
                      onPress={() => {
                        dispatch(updateUserSettings({spritz_wpm: wpmOption}));
                        setIsWpmMenuOpen(false);
                        startSpritzing();
                      }}
                    />
                  );
                })}
              </Menu>
            </View>
          </View>

          <View style={[styles.controls, {width: controlIconSize * 12}]}>{/* @TODO: don't like hardcoding this here */}
            {/* @TODO: removing for now...since this is on the selectionmenu, when passage changes, this is unmounted
            <IconButton
              style={styles.TODO}
              mode='contained'
              containerColor={buttonColor}
              icon='skip-previous'
              iconColor={buttonTextColor}
              disabled={!props.goToPreviousPassage}
              size={controlIconSize}
              onPress={() => {
                props.goToPreviousPassage();
                // @TODO: don't love that these are coupled...needed right now because of setInterval {
                currVerseNumber.current = 1;
                setCurrVerseNumber(1);
                // @TODO: }
                wordOffset.current = 0;
                startSpritzing(' '); // @TODO: this is called before passageVerses changes, so the wrong first word displays until the timer runs out; should pass in first word of previous chapter
              }}
            />
            */}
            <IconButton
              style={styles.TODO}
              mode='contained'
              containerColor={buttonColor}
              icon='rewind'
              iconColor={buttonTextColor}
              size={controlIconSize}
              onPress={() => {
                // @TODO: don't love that these are coupled...needed right now because of setInterval {
                const newVal = Math.max(props.passage.startVerseNumber, currVerseNumber.current - (wordOffset.current < 8 ? 1 : 0)); // @TODO: 4 is magic number...maybe make relative to spritzWpm?
                currVerseNumber.current = newVal;
                setCurrVerseNumber(newVal);
                // @TODO: }
                wordOffset.current = 0;
                startSpritzing();
              }}
            />
            <IconButton
              style={styles.TODO}
              mode='contained'
              containerColor={buttonColor}
              icon={_isPaused ? 'play' : 'pause'}
              iconColor={buttonTextColor}
              disabled={currVerseNumber.current > props.passage.endVerseNumber}
              size={controlIconSize}
              onPress={() => {
                if (_isPaused) {
                  startSpritzing();
                } else {
                  stopSpritzing();
                }
              }}
            />
            <IconButton
              style={styles.TODO}
              mode='contained'
              containerColor={buttonColor}
              icon='fast-forward'
              iconColor={buttonTextColor}
              disabled={currVerseNumber.current >= props.passage.endVerseNumber}
              size={controlIconSize}
              onPress={() => {
                // @TODO: don't love that these are coupled...needed right now because of setInterval {
                const newVal = Math.min(props.passage.endVerseNumber, currVerseNumber.current + 1);
                currVerseNumber.current = newVal;
                setCurrVerseNumber(newVal);
                // @TODO: }
                wordOffset.current = 0;
                startSpritzing();
              }}
            />
            {/* @TODO: removing for now...since this is on the selectionmenu, when passage changes, this is unmounted
            <IconButton
              style={styles.TODO}
              mode='contained'
              containerColor={buttonColor}
              icon='skip-next'
              iconColor={buttonTextColor}
              disabled={!props.goToNextPassage}
              size={controlIconSize}
              onPress={() => {
                props.goToNextPassage();
                // @TODO: don't love that these are coupled...needed right now because of setInterval {
                currVerseNumber.current = 1;
                setCurrVerseNumber(1);
                // @TODO: }
                wordOffset.current = 0;
                startSpritzing(' '); // @TODO: this is called before passageVerses changes, so the wrong first word displays until the timer runs out; should pass in first word of next chapter
              }}
            />
            */}
          </View>
        </View>

        <ScrollView
          style={styles.tableOfContentsContainer}
          contentContainerStyle={styles.tableOfContents}
        >
          {headingsInPassageByVerseOffset.map((headingData) => {
            return (
              <View
                key={headingData.verseNumber}
                style={styles.sectionWrapper}
              >
                {/* @TODO:
                <IconButton
                  mode='outlined'
                  icon='book'
                  iconColor={buttonTextColor}
                  size={14}
                  onPress={() => {
                    const chapterId = CPassage.getChapterByBookIdAndNumber(props.passage.book_id, props.passage.chapter_number).id;
                    dispatch(addToReadingHistory(chapterId));
                    navigation.navigate(READ_ROUTE, {
                      chapterId,
                      contextPassage: new CPassage(
                        -1,
                        props.passage.translationId,
                        props.passage.bookId,
                        props.passage.chapterNumber,
                        headingData.verseNumber,
                        headingData.verseNumber + headingData.numVersesInSection - 1,
                      ),
                    });
                  }}
                />
                */}
                <TouchableOpacity
                  style={styles.headingWrapper}
                  onPress={() => {
                    // @TODO: don't love that these are coupled...needed right now because of setInterval {
                    const newVal = headingData.verseNumber;
                    currVerseNumber.current = newVal;
                    setCurrVerseNumber(newVal);
                    // @TODO: }
                    wordOffset.current = 0;
                    startSpritzing();
                  }}
                >
                  <Text style={styles.headingText}>{headingData.headingText || ' '}</Text>
                  <View style={styles.sectionProgressWrapper}>
                    {Array.from({length: headingData.numVersesInSection}).map((__, i) => {
                      return (
                        <View
                          key={i}
                          style={[
                            styles.verseDot,
                            _currVerseNumber > (headingData.verseNumber + i) && styles.verseDotComplete,
                            _currVerseNumber === (headingData.verseNumber + i) && styles.verseDotInProgress,
                          ]}
                        />
                      );
                    })}
                  </View>
                </TouchableOpacity>
              </View>
            );
          })}
        </ScrollView>

        {/* @TODO:
        <View style={styles.otherOptionsContainer}>
          <Text>TODO: option to pause at headings</Text>
          <Text>TODO: option to pause between chapters</Text>
          <Text>TODO: option to repeat curret verse</Text>
          <Text>TODO: option to repeat current passage</Text>
        </View>
        */}
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          mode='contained'
          onPress={() => handleClose()}
        >
          {i18n('close')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class SpritzDialogProps {
  onClose: () => void;
  passage: any;

  goToPreviousPassage: () => void; // @TODO: verify signature
  goToNextPassage: () => void; // @TODO: verify signature
  assignmentId: number;
};

export default SpritzDialog;
